import { bookingModes, saleEndAnchors } from "@/domain/activity/types";
import type {
  Activity,
  ActivityCategory,
  ActivitySubcategory,
  BookingMode,
  SaleEndAnchor,
} from "@/domain/activity/types";
import { readCustomFieldDefinitions } from "./custom-fields.mapper";
import { fromMinorUnits } from "./money.mapper";

export interface ActivityPayload {
  id: number;
  businessId: number;
  name: string;
  description: string | null;
  priceOriginal: number | null;
  imageUrl: string | null;
  reservationUrl: string | null;
  subcategoryId: number | null;
  bookingMode: string;
  customFields?: unknown;
  defaultPriceDiscountedMinorUnits?: number | null;
  defaultCapacity?: number | null;
  saleLeadTimeHours?: number | null;
  saleEndOffsetMinutes?: number | null;
  saleEndAnchor?: string | null;
  defaultDurationMinutes?: number | null;
  requiresConfirmation?: boolean | null;
}

export interface TaxonomyPayload {
  categories: {
    id: number;
    name: string;
    slug: string;
    displayName: string;
    sortOrder: number;
  }[];
  subcategories: {
    id: number;
    categoryId: number;
    categorySlug: string;
    name: string;
    sortOrder: number;
  }[];
}

function isBookingMode(value: string): value is BookingMode {
  return (bookingModes as readonly string[]).includes(value);
}

function isSaleEndAnchor(value: string): value is SaleEndAnchor {
  return (saleEndAnchors as readonly string[]).includes(value);
}

/** An anchor this portal does not know takes its offset with it: the offset
 *  counted from somewhere unknown is not a sale close it can show or edit. */
export function readSaleClose(
  payload: Pick<ActivityPayload, "saleEndOffsetMinutes" | "saleEndAnchor">,
) {
  const { saleEndAnchor, saleEndOffsetMinutes } = payload;
  if (!saleEndAnchor || !isSaleEndAnchor(saleEndAnchor) || saleEndOffsetMinutes == null) {
    return { saleEndOffsetMinutes: null, saleEndAnchor: null };
  }
  return { saleEndOffsetMinutes, saleEndAnchor };
}

export function payloadToActivity(payload: ActivityPayload): Activity {
  return {
    id: payload.id,
    businessId: payload.businessId,
    name: payload.name,
    description: payload.description,
    subcategoryId: payload.subcategoryId,
    priceOriginal: payload.priceOriginal,
    reservationUrl: payload.reservationUrl,
    bookingMode: isBookingMode(payload.bookingMode) ? payload.bookingMode : "session",
    imageUrl: payload.imageUrl,
    customFields: readCustomFieldDefinitions(payload.customFields, "activities.custom_fields"),
    defaultPriceDiscounted: fromMinorUnits(payload.defaultPriceDiscountedMinorUnits ?? null),
    defaultCapacity: payload.defaultCapacity ?? null,
    saleLeadTimeHours: payload.saleLeadTimeHours ?? null,
    ...readSaleClose(payload),
    defaultDurationMinutes: payload.defaultDurationMinutes ?? null,
    requiresConfirmation: payload.requiresConfirmation ?? false,
  };
}

function payloadToCategory(payload: TaxonomyPayload["categories"][number]): ActivityCategory {
  return {
    id: payload.id,
    slug: payload.slug,
    name: payload.name,
    displayName: payload.displayName,
    sortOrder: payload.sortOrder,
  };
}

/** The two lists arrive in different orders: `categories` is the server's chosen
 *  order, `subcategories` is grouped by category id. The picker groups in
 *  arrival order, so the categories list is replayed onto the subcategories here
 *  — that keeps the order the server decided instead of re-deriving one that
 *  disagrees with every other client. The sort is stable, so a category's own
 *  subcategories keep the order they arrived in, and a subcategory whose
 *  category the taxonomy omits falls to the end. A subcategory's `name` is its
 *  Czech label; the taxonomy carries no machine name for it. */
export function payloadToSubcategories(payload: TaxonomyPayload): ActivitySubcategory[] {
  const categories = new Map<number, ActivityCategory>(
    payload.categories.map((category) => [category.id, payloadToCategory(category)]),
  );
  const positionOf = new Map(payload.categories.map((category, index) => [category.id, index]));
  const afterEveryCategory = payload.categories.length;
  return payload.subcategories
    .map((subcategory) => ({
      id: subcategory.id,
      categoryId: subcategory.categoryId,
      displayName: subcategory.name,
      sortOrder: subcategory.sortOrder,
      category: categories.get(subcategory.categoryId) ?? null,
    }))
    .sort(
      (a, b) =>
        (positionOf.get(a.categoryId) ?? afterEveryCategory) -
        (positionOf.get(b.categoryId) ?? afterEveryCategory),
    );
}
